import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { interactiveServiceFetch } from '../../services/interactiveServiceFetch';
import { INTERACTIVE_SERVICE_BASE_URL } from '../constants';
import css from './SessionDebugPanel.css';

const SessionDebugPanel = ({ sessionId, refreshKey = 0 }) => {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!sessionId) return;
        setError(null);
        const controller = new AbortController();
        const loadDebugInfo = async () => {
            try {
                const res = await interactiveServiceFetch(
                    `${INTERACTIVE_SERVICE_BASE_URL}/tracing/sessions/${sessionId}`,
                    { signal: controller.signal },
                );
                if (!res.ok) {
                    setError(`Failed to load debug info (${res.status})`);
                    return;
                }
                const body = await res.json();
                setData(body);
            } catch (err) {
                if (err && err.name !== 'AbortError') {
                    setError(`Failed to load debug info: ${err.message}`);
                }
            }
        };
        loadDebugInfo();
        return () => controller.abort();
    }, [sessionId, refreshKey]);

    useEffect(() => { setData(null); }, [sessionId]);

    if (!sessionId) return null;
    if (error) return <section aria-label="Session debug" className={css.panel}><span className={css.error}>{error}</span></section>;
    if (!data) return null;

    return (
        <section aria-label="Session debug" className={css.panel}>
            <div className={css.header}>Session debug</div>
            <div className={css.body}>
                <div className={css.row}>
                    <span className={css.label}>User</span>
                    <code className={css.code}>{data.user_email}</code>
                </div>
                {data.impersonated_user_email && (
                    <div className={css.row}>
                        <span className={css.label}>Impersonated as</span>
                        <code className={css.code}>{data.impersonated_user_email}</code>
                    </div>
                )}
                <div className={css.row}>
                    <span className={css.label}>Langfuse session</span>
                    {data.langfuse_session_url ? (
                        <a className={css.link} href={data.langfuse_session_url} target="_blank" rel="noopener noreferrer">
                            View session →
                        </a>
                    ) : (
                        <span className={css.muted}>not available</span>
                    )}
                </div>
                <div className={css.row}>
                    <span className={css.label}>Langfuse traces</span>
                    {data.langfuse_traces_url ? (
                        <a className={css.link} href={data.langfuse_traces_url} target="_blank" rel="noopener noreferrer">
                            View agent traces →
                        </a>
                    ) : (
                        <span className={css.muted}>not available</span>
                    )}
                </div>
                <div className={css.row}>
                    <span className={css.label}>S3 artifact store</span>
                    {data.s3_artifact_prefix
                        ? <code className={css.code}>{data.s3_artifact_prefix}</code>
                        : <span className={css.muted}>not available</span>}
                </div>
            </div>
        </section>
    );
};

SessionDebugPanel.propTypes = {
    sessionId: PropTypes.string,
    refreshKey: PropTypes.number,
};

export default SessionDebugPanel;
